import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-logo">🎙️ EchoMemoir</Link>
      </header>

      <main className="legal-main">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: June 20, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using EchoMemoir ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
            If you do not agree to these Terms, you may not use the Service. EchoMemoir reserves the right to update 
            these Terms at any time, and your continued use of the Service constitutes acceptance of any changes.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            EchoMemoir is a family storytelling platform that allows users to record, transcribe, enhance, and preserve 
            family stories. The Service includes audio recording, AI-powered transcription and enhancement, voice cloning 
            capabilities, photo and video storage and playback, family circle management, and story sharing features.
          </p>
        </section>

        <section>
          <h2>3. Account Registration</h2>
          <p>
            To use EchoMemoir, you must create an account with a valid email address and secure password. You are 
            responsible for maintaining the confidentiality of your account credentials and for all activities that 
            occur under your account. You must be at least 13 years of age to create an account. If you are under 18, 
            you must have parental or guardian consent to use the Service.
          </p>
        </section>

        <section>
          <h2>4. User Content</h2>
          <p>
            You retain ownership of all content you upload to EchoMemoir, including audio recordings, video recordings,
            stories, photos, and other materials ("User Content"). By uploading User Content, you grant EchoMemoir a
            limited, non-exclusive license to store, transcode, process, and display your content solely for the purpose
            of providing the Service to you and your designated family circle members.
          </p>
          <p>
            You represent that you have the right to share any content you upload, and that your content does not violate 
            the rights of any third party. You are solely responsible for the content you upload to the Service.
          </p>
        </section>

        <section>
          <h2>4a. Video Uploads</h2>
          <p>EchoMemoir supports video uploads as part of your family stories. By uploading a video, you acknowledge and agree that:</p>
          <ul>
            <li>Videos will be automatically transcoded into HLS (HTTP Live Streaming) format for efficient playback across devices. This does not alter the substantive content of your video.</li>
            <li>Transcoded video segments and thumbnails are stored on AWS S3 and delivered via AWS CloudFront using time-limited signed URLs. Original files may be temporarily retained during processing.</li>
            <li>You have obtained consent from all individuals who appear in the video, and you have the right to upload any copyrighted material contained in the video (including background music, footage, or other third-party content).</li>
            <li>Video uploads are subject to file size and format limits as posted on the Service, which may be updated from time to time.</li>
            <li>EchoMemoir does not review video content for rights clearance. You are solely responsible for ensuring your video uploads comply with all applicable laws and third-party rights.</li>
          </ul>
        </section>

        <section>
          <h2>5. AI Processing and Enhancement</h2>
          <p>
            EchoMemoir uses artificial intelligence to transcribe audio recordings and enhance stories. AI-generated 
            content is provided as a creative enhancement and may not perfectly represent the original recording. You 
            acknowledge that AI processing involves sending your content to third-party AI service providers (such as 
            OpenAI) for processing. EchoMemoir is not responsible for the accuracy of AI-generated transcriptions or 
            enhancements.
          </p>
        </section>

        <section>
          <h2>6. Voice Cloning</h2>
          <p>
            EchoMemoir offers voice cloning capabilities that create synthetic voice profiles from audio recordings. 
            By using this feature, you represent that you have obtained explicit consent from any individual whose 
            voice is being cloned, or that you are the individual whose voice is being recorded. Voice cloning data 
            is processed through third-party services and is subject to their respective terms and privacy policies.
          </p>
          <p>
            You agree not to use voice cloning features to impersonate others without their consent, create misleading 
            or fraudulent content, or for any unlawful purpose. EchoMemoir reserves the right to disable voice cloning 
            features for any account found in violation of these terms.
          </p>
        </section>

        <section>
          <h2>7. Family Circles and Sharing</h2>
          <p>
            Family circles allow you to share stories with invited members. As a vault keeper (administrator) of a 
            family circle, you are responsible for managing membership and ensuring that invited members are appropriate. 
            EchoMemoir is not responsible for how other members of your family circle use or share content within the circle.
          </p>
        </section>

        <section>
          <h2>8. Subscription and Payment</h2>
          <p>
            EchoMemoir offers free and paid subscription tiers. Paid features are subject to the pricing displayed at 
            the time of purchase. Subscriptions may be cancelled at any time, and access to paid features will continue 
            until the end of the current billing period. Refunds are handled on a case-by-case basis and may be requested 
            by contacting support@echomemoir.ai.
          </p>
        </section>

        <section>
          <h2>9. Prohibited Conduct</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose or in violation of any applicable laws</li>
            <li>Upload video content containing third-party copyrighted material (music, footage, broadcasts) without the necessary rights or licenses</li>
            <li>Upload content that is defamatory, obscene, hateful, or otherwise objectionable</li>
            <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
            <li>Use automated tools to scrape, crawl, or collect data from the Service</li>
            <li>Interfere with or disrupt the Service or its infrastructure</li>
            <li>Use voice cloning features to create non-consensual impersonations</li>
            <li>Share or distribute other users' content without their permission</li>
          </ul>
        </section>

        <section>
          <h2>10. Termination</h2>
          <p>
            You may delete your account at any time through the Settings page. EchoMemoir reserves the right to suspend 
            or terminate accounts that violate these Terms. Upon account deletion, your personal data will be removed 
            in accordance with our <Link to="/privacy">Privacy Policy</Link>. Stories shared within family circles may 
            be retained for other members' access but will be disassociated from your account.
          </p>
        </section>

        <section>
          <h2>11. Disclaimer of Warranties</h2>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. 
            EchoMemoir does not guarantee that the Service will be uninterrupted, error-free, or secure. We do not warrant 
            the accuracy of AI-generated transcriptions, enhancements, or voice synthesis.
          </p>
        </section>

        <section>
          <h2>12. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, EchoMemoir shall not be liable for any indirect, incidental, special, 
            consequential, or punitive damages arising from your use of the Service, including but not limited to loss of 
            data, loss of profits, or interruption of service. Our total liability shall not exceed the amount you paid 
            for the Service in the twelve months preceding the claim.
          </p>
        </section>

        <section>
          <h2>13. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the State of California, 
            United States, without regard to conflict of law provisions. Any disputes arising from these Terms shall 
            be resolved in the courts of California.
          </p>
        </section>

        <section>
          <h2>14. Contact</h2>
          <p>
            If you have questions about these Terms, please contact us at{' '}
            <a href="mailto:support@echomemoir.ai">support@echomemoir.ai</a>.
          </p>
        </section>
      </main>

      <footer className="legal-footer">
        <Link to="/privacy">Privacy Policy</Link>
        <span>·</span>
        <Link to="/terms">Terms of Service</Link>
        <span>·</span>
        <span>© {new Date().getFullYear()} EchoMemoir</span>
      </footer>
    </div>
  );
};

export default TermsOfService;